import imageCompression from "browser-image-compression";

const withTimeout = <T,>(p: Promise<T>, ms: number, label: string): Promise<T> =>
  Promise.race<T>([
    p,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`${label} a expiré après ${Math.round(ms / 1000)}s`)), ms)
    ),
  ]);

export interface CompressOptions {
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  initialQuality?: number;
  timeoutMs?: number;
}

export const ACCEPTED_IMAGE_MIME =
  "image/png,image/jpeg,image/jpg,image/webp";

const ACCEPTED_EXT_RE = /\.(jpe?g|png|webp)$/i;
const HEIC_RE = /(heic|heif)/i;

export interface ValidationResult {
  ok: boolean;
  error?: string;
}

export const validateImageFile = (
  file: File,
  opts: { maxOriginalMB?: number } = {}
): ValidationResult => {
  const { maxOriginalMB = 20 } = opts;
  const nameOk = ACCEPTED_EXT_RE.test(file.name);
  const mimeOk = /^image\/(png|jpe?g|webp)$/i.test(file.type);
  if (!nameOk && !mimeOk) {
    return {
      ok: false,
      error: "Format non supporté. Utilisez JPG, PNG ou WebP.",
    };
  }
  const sizeMB = file.size / (1024 * 1024);
  if (sizeMB > maxOriginalMB) {
    return {
      ok: false,
      error: `Image trop lourde (${sizeMB.toFixed(1)} Mo). Maximum ${maxOriginalMB} Mo.`,
    };
  }
  return { ok: true };
};

const isHeic = (file: File) =>
  HEIC_RE.test(file.type) || /\.(heic|heif)$/i.test(file.name);

export const convertHeicIfNeeded = async (file: File): Promise<File> => {
  if (!isHeic(file)) return file;
  try {
    const mod = await import("heic2any");
    const heic2any = (mod as any).default || (mod as any);
    const out = await withTimeout(
      heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 }) as Promise<Blob | Blob[]>,
      45_000,
      "La conversion HEIC"
    );
    const blob = Array.isArray(out) ? out[0] : out;
    const baseName = file.name.replace(/\.[^.]+$/, "") || "image";
    return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
  } catch (err) {
    console.error("[convertHeicIfNeeded] failed:", err);
    throw new Error(
      "Format HEIC non supporté sur ce navigateur. Convertissez l'image en JPG ou PNG."
    );
  }
};

/**
 * Compress an image to WebP. Web workers are disabled because they hang
 * inside sandboxed/cross-origin iframes (Lovable preview). Falls back to the
 * original file if compression fails so the upload still proceeds.
 */
export const compressToWebP = async (file: File, opts: CompressOptions = {}): Promise<File> => {
  const {
    maxSizeMB = 0.4,
    maxWidthOrHeight = 1920,
    initialQuality = 0.82,
    timeoutMs = 30_000,
  } = opts;

  // Convert HEIC → JPEG first so the canvas-based compressor can decode it.
  const input = await convertHeicIfNeeded(file);

  if (!input.type.startsWith("image/")) return input;

  try {
    const compressed = await withTimeout(
      imageCompression(input, {
        maxSizeMB,
        maxWidthOrHeight,
        useWebWorker: false,
        fileType: "image/webp",
        initialQuality,
      }),
      timeoutMs,
      "La compression de l'image"
    );
    const baseName = input.name.replace(/\.[^.]+$/, "") || "image";
    return new File([compressed], `${baseName}.webp`, { type: "image/webp" });
  } catch (err) {
    console.warn("[compressToWebP] fallback to original file:", err);
    return input;
  }
};

/** Race any promise against a timeout — surfaces a clear error instead of hanging. */
export const withUploadTimeout = <T,>(p: Promise<T>, ms = 60_000): Promise<T> =>
  withTimeout(p, ms, "L'upload");